import { DEFAULT_LOCALE, LOCALE, TranslatedEntry } from './i18n';
import slugify from './slug';
import { config } from './config';
import { BYPASS_CACHE, TEST_ADS } from '@crazygames/utils/constants';
import { getLocaleCategoryPrefix, getLocaleGamePrefix, getLocaleTagPrefix } from '@crazygames/countries/url-utils';

export function getBust() {
  return Math.random().toString(36).substr(2, 5);
}

interface GameUrlOptions {
  cacheBust?: boolean;
  testAds?: boolean;
  sdkDebug?: boolean;
  noWaitTime?: boolean;
  isKids?: boolean;
  secretSkipPreroll?: boolean;
  ruffleDebug?: boolean;
  forceMobile?: boolean;
}

interface TagCategoryUrlOptions {
  cacheBust?: boolean;
}

function getBaseUrl(locale: LOCALE) {
  return (config.domains[locale] || config.domains[DEFAULT_LOCALE]).base;
}

function getPathnameUrl(locale: LOCALE) {
  return (config.domains[locale] || config.domains[DEFAULT_LOCALE]).pathname;
}

export function getGameUrl(gameSlug: string, options: GameUrlOptions, locale: LOCALE = 'en_US') {
  const { cacheBust, testAds, sdkDebug, noWaitTime, isKids = false, secretSkipPreroll, ruffleDebug = false, forceMobile } = options;
  let url;
  if (!isKids) {
    const base = getBaseUrl(locale);
    const pathname = getPathnameUrl(locale);
    const gamePrefix = getLocaleGamePrefix(locale);
    url = new URL(`${base}${pathname}/${gamePrefix}/${gameSlug}`);
  } else {
    url = new URL(`${config.kidSiteDomain.base}/game/${gameSlug}`);
  }
  const { searchParams } = url;
  if (cacheBust) {
    const bustStr = getBust();
    searchParams.set(BYPASS_CACHE, bustStr);
  }
  if (testAds) {
    searchParams.set(TEST_ADS, 'true');
  }
  if (sdkDebug) {
    searchParams.set('sdk_debug', 'true');
  }
  if (noWaitTime) {
    searchParams.set('noWaitTime', 'true');
  }
  if (secretSkipPreroll) {
    searchParams.set('secretSkipPreroll', 'true');
  }
  if (ruffleDebug) {
    searchParams.set('ruffle_debug', 'true');
  }
  if (forceMobile) {
    searchParams.set('forceMobile', 'true');
  }
  return decodeURIComponent(url.toString());
}

export function getTagUrl(tagSlugTrl: TranslatedEntry[], options: TagCategoryUrlOptions, locale: LOCALE = 'en_US') {
  const base = getBaseUrl(locale);
  const pathname = getPathnameUrl(locale);
  const tagPrefix = getLocaleTagPrefix(locale);
  const tagSlug = (tagSlugTrl.find((slugTrl) => slugTrl.locale === locale) || {}).text;
  const url = new URL(`${base}${pathname}/${tagPrefix}/${tagSlug}`);
  const { searchParams } = url;

  if (options.cacheBust) {
    const bustStr = getBust();
    searchParams.set(BYPASS_CACHE, bustStr);
  }
  return url.toString();
}

export function getCategoryUrl(categorySlugTrl: TranslatedEntry[], options: TagCategoryUrlOptions, locale: LOCALE = 'en_US') {
  const base = getBaseUrl(locale);
  const pathname = getPathnameUrl(locale);
  const categoryPrefix = getLocaleCategoryPrefix(locale);
  const categorySlug = (categorySlugTrl.find((slugTrl) => slugTrl.locale === locale) || {}).text;
  const url = new URL(`${base}${pathname}/${categoryPrefix}/${categorySlug}`);
  const { searchParams } = url;

  if (options.cacheBust) {
    const bustStr = getBust();
    searchParams.set(BYPASS_CACHE, bustStr);
  }
  return url.toString();
}

function isPrefixedUrl<T>(url: string, prefix: string, locale: LOCALE, type: T) {
  const base = getBaseUrl(locale);
  const pathname = getPathnameUrl(locale);

  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.hostname !== base) {
      return false;
    }
    const pathnamePrefix = `/${prefix}/`;
    const parsedPathname = parsedUrl.pathname.replace(pathname, '');
    if (!parsedPathname.startsWith(pathnamePrefix)) {
      return false;
    }
    const slug = parsedPathname.substring(pathnamePrefix.length);
    if (slug !== slugify(slug)) {
      return false;
    }
    return { slug, type };
  } catch {
    return null;
  }
}

export function isTagUrl(url: string, locale: LOCALE) {
  const prefix = getLocaleTagPrefix(locale);
  return isPrefixedUrl<'tag'>(url, prefix, locale, 'tag');
}

export function isCategoryUrl(url: string, locale: LOCALE) {
  const prefix = getLocaleCategoryPrefix(locale);
  return isPrefixedUrl<'category'>(url, prefix, locale, 'category');
}

export function isGameUrl(url: string, locale: LOCALE) {
  const prefix = getLocaleGamePrefix(locale);
  return isPrefixedUrl<'game'>(url, prefix, locale, 'game');
}
